/**
 * Module: screens/InboxScreen
 * Role: Buyer inbox — list of synchronized notifications with tap-to-read
 *       and swipe-to-delete. Mirrors Telegram push history.
 *
 * Uses:
 *   - components/atoms:IOSScreen, PageHeader, EmptyState, Skeleton
 *   - api/notifications:useNotifications, useMarkRead, useDeleteNotification
 *   - hooks/useSwipe:useSwipe
 *   - react-router-dom:useNavigate
 *   - styles/tokens:P, S, T
 *
 * Used by:
 *   - App:default (Route "/inbox")
 *
 * ADR-022 sibling-comment: `cmap` keys are symmetric with:
 *   - miniapp/src/api/notifications.ts:NotificationKind (TS type)
 *   - src/api/v2/buyer/_notification_schemas.py:NotificationItem.kind (Python Literal)
 *   - supabase/migrations/20260518122000_ftr1034_buyer_notifications_table.sql (DB CHECK)
 *
 * Glossary: ai/glossary/buyer.md
 */
import { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { IOSScreen, PageHeader, EmptyState, Skeleton } from '../components/atoms';
import {
  useNotifications,
  useMarkRead,
  useDeleteNotification,
  type NotificationItem,
  type NotificationKind,
} from '../api/notifications';
import { useSwipe } from '../hooks/useSwipe';
import { P, S, T } from '../styles/tokens';

// ADR-022 sibling-comment: cmap keys must match NotificationKind (api/notifications.ts),
// Python Literal (_notification_schemas.py), and DB CHECK (20260518122000 migration).
const cmap: Record<NotificationKind, string> = {
  success: P.success,
  brand:   P.jade,
  error:   P.terracotta,
  warn:    P.warn,
  muted:   S.tertiaryLabel,
};

// ---------------------------------------------------------------------------
// Tiny date formatter — no external dep
// Compact time only for same-day, short date+time otherwise
// ---------------------------------------------------------------------------

function formatRelativeDate(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    if (sameDay) {
      return d.toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// NotifRow — single notification row with swipe-to-delete
// ---------------------------------------------------------------------------

interface NotifRowProps {
  item: NotificationItem;
  onRead: (id: string) => void;
  onDelete: (id: string) => void;
  isLast: boolean;
}

function NotifRow({ item, onRead, onDelete, isLast }: NotifRowProps) {
  const isRead = item.read_at !== null;
  const { offsetX, swiped, dragging, handlers } = useSwipe({
    onTap: () => { if (!isRead) onRead(item.id); },
  });

  const dotColor = cmap[item.kind] ?? S.tertiaryLabel;

  const rowOuter: CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
  };

  const deleteBtn: CSSProperties = {
    position: 'absolute',
    right: 0, top: 0, bottom: 0,
    width: 88,
    background: P.terracotta,
    color: '#fff',
    border: 'none',
    // Prototype: ...T.subheadline, fontWeight: 600
    fontFamily: T.fontBody,
    fontSize: 15,
    fontWeight: 600,
    letterSpacing: -0.24,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const rowInner: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '14px 16px',
    // Prototype uses P.bg (warm linen) for row background — matches page warmth
    background: P.bg,
    borderBottom: isLast ? 'none' : `0.5px solid ${S.separator}`,
    opacity: isRead ? 0.65 : 1,
    transform: `translateX(${offsetX}px)`,
    transition: dragging ? 'none' : 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
    touchAction: 'pan-y',
    cursor: 'pointer',
    userSelect: 'none',
  };

  return (
    <div style={rowOuter}>
      {swiped && (
        <button
          style={deleteBtn}
          onClick={() => onDelete(item.id)}
          aria-label="Удалить уведомление"
        >
          Удалить
        </button>
      )}
      <div style={rowInner} {...handlers}>
        {/* Kind dot — prototype: 10×10 with opacity fade on read */}
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: dotColor,
            flexShrink: 0,
            marginTop: 7,
            opacity: isRead ? 0.3 : 1,
            transition: 'opacity 0.2s ease',
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title row — title left, compact timestamp right (prototype pattern) */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 8,
              marginBottom: 3,
            }}
          >
            <span
              style={{
                // Prototype: ...T.body, fontWeight: read ? 500 : 600
                fontFamily: T.fontBody,
                fontSize: 17,
                fontWeight: isRead ? 500 : 600,
                letterSpacing: -0.43,
                color: S.label,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              {item.title}
            </span>
            <span
              style={{
                font: T.caption,
                color: S.tertiaryLabel,
                flexShrink: 0,
              }}
            >
              {formatRelativeDate(item.created_at)}
            </span>
          </div>
          {/* Body text */}
          <div
            style={{
              // Prototype: ...T.subheadline, color: S.secondaryLabel
              fontFamily: T.fontBody,
              fontSize: 15,
              fontWeight: 400,
              letterSpacing: -0.24,
              color: S.secondaryLabel,
              lineHeight: 1.45,
            }}
          >
            {item.body}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// InboxScreen
// ---------------------------------------------------------------------------

export default function InboxScreen() {
  const navigate = useNavigate();
  const { data, isPending, isError } = useNotifications();
  const markRead = useMarkRead();
  const deleteNotif = useDeleteNotification();

  return (
    <IOSScreen>
      <PageHeader
        title="Уведомления"
        subtitle="Синхронизовано с чатом бота"
        onBack={() => navigate(-1)}
      />

      <div style={{ height: 8 }} />

      {isPending && (
        <div>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                padding: '14px 16px',
                borderBottom: i < 3 ? `0.5px solid ${S.separator}` : 'none',
                background: P.bg,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <Skeleton w="55%" h={14} variant="text" />
                <Skeleton w={40} h={12} variant="text" />
              </div>
              <Skeleton w="88%" h={12} variant="text" />
            </div>
          ))}
        </div>
      )}

      {isError && (
        <EmptyState title="Не удалось загрузить уведомления" subtitle="Попробуйте позже" />
      )}

      {!isPending && !isError && data && data.items.length === 0 && (
        <EmptyState title="Пока ничего нет" />
      )}

      {!isPending && !isError && data && data.items.length > 0 && (
        <>
          <div>
            {data.items.map((item, i) => (
              <NotifRow
                key={item.id}
                item={item}
                isLast={i === data.items.length - 1}
                onRead={(id) => markRead.mutate(id)}
                onDelete={(id) => deleteNotif.mutate(id)}
              />
            ))}
          </div>
          {/* Swipe hint — prototype: caption at bottom center */}
          <div
            style={{
              font: T.captionAlt,
              color: S.tertiaryLabel,
              textAlign: 'center',
              marginTop: 22,
              padding: '0 24px 32px',
            }}
          >
            Свайп влево — удалить
          </div>
        </>
      )}
    </IOSScreen>
  );
}
